import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IReservationDetails } from 'shared-ui'
import { AxiosError } from 'axios'
import API from 'api/client'

interface ReservationResponse {
  data: IReservationDetails
}

interface VerifyReservationResponse {
  message: string
}

interface CardMoveResponse {
  success: boolean
  message: string
}

interface ReservationError {
  message: string
  code: number
  requestId: string
  user_message: string
}

interface ReservationState {
  fetchStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  verifyStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  verifyMessage: string | null
  error: string | null
  reservationDetails: IReservationDetails | null
}

const initialState: ReservationState = {
  fetchStatus: 'idle',
  verifyStatus: 'idle',
  verifyMessage: null,
  error: null,
  reservationDetails: null,
}

export const fetchReservationDetails = createAsyncThunk<
  ReservationResponse,
  string,
  { rejectValue: ReservationError }
>(
  'reservation/fetchReservationDetails',
  async (reservationNumber, { rejectWithValue }) => {
    try {
      const response = await API.get<ReservationResponse>(
        `/reservation/${reservationNumber}`
      )
      return response.data
    } catch (err) {
      const error: AxiosError<ReservationError> =
        err as AxiosError<ReservationError>
      if (!error.response) {
        throw error
      }
      return rejectWithValue(error.response.data)
    }
  }
)

export const verifyReservation = createAsyncThunk<
  VerifyReservationResponse,
  { uuid: string; pin: string },
  { rejectValue: ReservationError }
>(
  'reservation/verifyReservation',
  async ({ uuid, pin }, { rejectWithValue }) => {
    try {
      const response = await API.post<VerifyReservationResponse>(
        `/reservation/${uuid}/verify`,
        { pin }
      )
      return response.data
    } catch (err) {
      const error: AxiosError<ReservationError> =
        err as AxiosError<ReservationError>
      if (!error.response) {
        throw error
      }
      return rejectWithValue(error.response.data)
    }
  }
)

export const moveToGate = createAsyncThunk<
  CardMoveResponse,
  { bookingUuid: string; numberOfCards: number },
  { rejectValue: ReservationError }
>(
  'reservation/moveToGate',
  async ({ bookingUuid, numberOfCards }, { rejectWithValue }) => {
    try {
      const response = await API.put<CardMoveResponse>(
        `/card/move-to-gate/${bookingUuid}`,
        { number_of_cards: numberOfCards }
      )
      return response.data
    } catch (err) {
      const error: AxiosError<ReservationError> =
        err as AxiosError<ReservationError>
      if (!error.response) {
        throw error
      }
      return rejectWithValue(error.response.data)
    }
  }
)

const reservationSlice = createSlice({
  name: 'reservation',
  initialState,
  reducers: {
    resetReservationStatus(state: ReservationState) {
      state.fetchStatus = 'idle'
      state.verifyStatus = 'idle'
      state.error = null
      state.reservationDetails = null
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchReservationDetails.pending, (state: ReservationState) => {
        state.fetchStatus = 'loading'
        state.error = null
      })
      .addCase(
        fetchReservationDetails.fulfilled,
        (
          state: ReservationState,
          action: PayloadAction<ReservationResponse>
        ) => {
          state.fetchStatus = 'succeeded'
          state.reservationDetails = action.payload.data
        }
      )
      .addCase(
        fetchReservationDetails.rejected,
        (
          state: ReservationState,
          action: PayloadAction<ReservationError | undefined>
        ) => {
          state.fetchStatus = 'failed'
          state.error = action.payload?.message || null
        }
      )
      .addCase(verifyReservation.pending, (state: ReservationState) => {
        state.verifyStatus = 'loading'
        state.error = null
      })
      .addCase(
        verifyReservation.fulfilled,
        (
          state: ReservationState,
          action: PayloadAction<VerifyReservationResponse | undefined>
        ) => {
          state.verifyStatus = 'succeeded'
          state.verifyMessage = action.payload?.message ?? null
        }
      )
      .addCase(
        verifyReservation.rejected,
        (
          state: ReservationState,
          action: PayloadAction<ReservationError | undefined>
        ) => {
          state.verifyStatus = 'failed'
          state.error = action.payload?.message || null
        }
      )
      .addCase(moveToGate.pending, (state: ReservationState) => {
        state.fetchStatus = 'loading'
        state.error = null
      })
      .addCase(moveToGate.fulfilled, (state: ReservationState) => {
        state.fetchStatus = 'succeeded'
      })
      .addCase(
        moveToGate.rejected,
        (
          state: ReservationState,
          action: PayloadAction<ReservationError | undefined>
        ) => {
          state.fetchStatus = 'failed'
          state.error = action.payload?.message || null
        }
      )
  },
})

export const { resetReservationStatus } = reservationSlice.actions
export default reservationSlice.reducer
